import React, {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from "react";

import {
  Box,
  Column,
  CopyIcon,
  Row,
  SparkleIcon,
  SyncIcon,
  Text,
  TraitIcon,
  WarningIcon,
} from "@hightouch/htui";
import { type Condition, ConditionType } from "src/types/visual";
import * as analytics from "src/lib/analytics";
import { useHighlightedConditionContext } from "src/components/explore/context/highlighted-condition-context";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";
import { getConditionLabel } from "src/components/explore/filter-popover/utils";
import { useFilterInsightsNudge } from "src/components/explore/visual/use-filter-insights-nudge";

import {
  AndOrToggleButton,
  DeleteButton,
  TextButton,
  XButton,
} from "./condition-buttons";
import { GenerateFilterInsightsButton } from "./generate-filter-insights-button";
import { GroupIndicatorBar } from "./group-indicator-bar";
import { CompactOperatorRail } from "./compact-operator-rail";
import { isPresent } from "ts-extras";
import { useDropTarget } from "src/components/explore/draggable";
import { waterfallHeight } from "./constants";
import {
  FilterActionsMenu,
  makeAddNestedGroupMenuItem,
  type FilterActionsMenuItem,
} from "./filter-actions-menu";

interface ConditionWrapperBarPartOnlyProps {
  children: ReactNode;
  footer?: ReactNode;
  index?: number;
  condition?: Condition;
  conditionType: ConditionType.And | ConditionType.Or;
  onRemove?: () => void;
  menuItems?: FilterActionsMenuItem[];
  disableDrag?: boolean;
  forceDragStyles?: boolean;
  /** Render the compact rail and tighter padding instead of the full-width
   * indicator bar. Decided by the caller, which knows the nesting level —
   * see `isCompactCondition`. */
  isCompact?: boolean;
  isDisabled?: boolean;
  /** Toggles the whole group's operator. In compact this is what the rail
   * chip calls; otherwise the AND/OR row below the condition owns it. */
  onToggleAndOr?: () => void;
}

interface ConditionWrapperProps
  extends Omit<ConditionWrapperBarPartOnlyProps, "menuItems"> {
  "data-testid"?: string;
  disableGrouping?: boolean;
  isRealtimeAudience?: boolean;
  onAddTrait?: () => void;
  onAddAudienceTrait?: () => void;
  onToggleAndOr: () => void;
  onDuplicate: () => void;
  onGroup: () => void;
  onUngroup: (() => void) | undefined;
}

export const ConditionWrapper: FC<Readonly<ConditionWrapperProps>> = (
  props,
) => {
  const {
    index = -1,
    isDisabled = false,
    conditionType,
    isRealtimeAudience = false,
    "data-testid": dataTestId,
    disableGrouping = false,
    onAddTrait,
    onAddAudienceTrait,
    onToggleAndOr,
    onDuplicate,
    onGroup,
    onUngroup,
    disableDrag,
    isCompact = false,
  } = props;
  const { ref } = useDropTarget({
    index,
    disable: disableDrag,
    dropPlacement: "split",
  });
  const nudgeAgent = useFilterInsightsNudge();
  const { relationships, relatedAudiences, journeys, traits } =
    useFilterableOptionsContext();

  const menuItems = [
    makeAddNestedGroupMenuItem({
      currentConditionType: conditionType,
      onGroup,
      isDisabled: disableGrouping,
    }),
    {
      label: "Duplicate filter",
      description: "Create a copy of this filter",
      icon: CopyIcon,
      onClick: onDuplicate,
    },
    !isRealtimeAudience
      ? {
          label: "Sync a trait from this filter",
          description: "Create a new audience trait based on this filter",
          icon: SyncIcon,
          onClick: onAddAudienceTrait,
        }
      : null,
    {
      label: "Save trait",
      description: "Create a new trait based on this filter",
      icon: TraitIcon,
      onClick: onAddTrait,
    },
    nudgeAgent && props.condition
      ? {
          label: "Add filter to chat",
          description: "Send this filter to the agent",
          icon: SparkleIcon,
          onClick: () => {
            const condition = props.condition;
            if (!condition) return;
            analytics.track("Audiences Agent Add Filter To Chat Clicked", {
              conditionType: condition.type,
            });
            nudgeAgent("", {
              focusedConditions: condition,
              focusedLabel: getConditionLabel(condition, {
                relationships,
                relatedAudiences,
                journeys,
                traits,
              }),
              autoSubmit: false,
            });
          },
        }
      : null,
  ].filter(isPresent);

  return (
    <Row width="100%" data-testid={dataTestId}>
      <Column flex={1} minWidth={0} ref={ref}>
        <Row flex={1} minWidth={0} mb={isCompact ? 2 : 4}>
          {/* Compact carries the operator in the rail, so it wants no actions
              menu — an empty item list renders nothing. Hiding it is also what
              makes nested groups unreachable, since its "Add group" entry is
              the only way to create one at this level. */}
          <ConditionWrapperBarPartOnly
            {...props}
            menuItems={isCompact ? [] : menuItems}
          />
        </Row>
        {!isCompact && (
          <Row align="center" gap={2} mb={4}>
            <AndOrToggleButton
              isDisabled={isDisabled}
              conditionType={conditionType}
              onClick={onToggleAndOr}
            />

            {onUngroup && !isDisabled && (
              <TextButton size="md" onClick={onUngroup}>
                Ungroup
              </TextButton>
            )}
          </Row>
        )}
      </Column>
    </Row>
  );
};

const highlightFlashSx = {
  animation: "condition-highlight-flash 1.4s ease-out 1 forwards",
  "@keyframes condition-highlight-flash": {
    "0%": { backgroundColor: "var(--chakra-colors-yellow-100)" },
    "100%": { backgroundColor: "white" },
  },
} as const;

export const ConditionWrapperBarPartOnly = (
  props: ConditionWrapperBarPartOnlyProps,
) => {
  const {
    conditionType,
    index,
    condition,
    footer = null,
    children,
    menuItems = [],
    onRemove = () => {},
    disableDrag,
    forceDragStyles,
    isCompact = false,
    isDisabled = false,
    onToggleAndOr,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const getContainerHeight = useCallback(
    () => containerRef.current?.offsetHeight,
    [],
  );

  const { highlightedConditionId } = useHighlightedConditionContext();
  const isHighlighted =
    !!condition?.id && condition.id === highlightedConditionId;
  const highlightRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isHighlighted) {
      highlightRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isHighlighted]);

  const { staleWarehouseConditions } = useQueryBuilderContext();
  const staleKind = condition?.id
    ? staleWarehouseConditions.get(condition.id)
    : undefined;

  return (
    <>
      {isCompact ? (
        <CompactOperatorRail
          conditionType={conditionType}
          isDisabled={isDisabled}
          // The leading condition has nothing to join, so it renders as a
          // bracket. Any other chip toggles the whole group.
          onToggle={(index ?? 0) > 0 ? onToggleAndOr : undefined}
        />
      ) : (
        <GroupIndicatorBar
          getContainerHeight={getContainerHeight}
          conditionType={conditionType}
          index={index}
          condition={condition}
          disableDrag={disableDrag}
          forceDragStyles={forceDragStyles}
        />
      )}
      <Column
        ref={highlightRef}
        flex={1}
        minWidth="max-content"
        bg="white"
        boxShadow="buttonShadowLight"
        borderBottomWidth="hairline"
        borderBottomStyle="solid"
        borderRightWidth="hairline"
        borderRightStyle="solid"
        borderTopWidth="hairline"
        borderTopStyle="solid"
        borderColor="base.border"
        borderBottomRightRadius={isCompact ? "lg" : "xl"}
        borderTopRightRadius={isCompact ? "lg" : "xl"}
        sx={isHighlighted ? highlightFlashSx : undefined}
      >
        {staleKind && (
          <Row align="center" gap={2} px={4} pt={3}>
            <Box
              as={WarningIcon}
              color="warning.base"
              fontSize="20px"
              flexShrink={0}
            />
            <Text size="sm" color="warning.base" fontWeight="medium">
              {staleKind === "added"
                ? "Definition added since last pull"
                : "Definition updated since last pull"}
            </Text>
          </Row>
        )}
        <Row
          ref={containerRef}
          minWidth="max-content"
          flexBasis="fit-content"
          gap={2}
          justify="space-between"
          // 6px of vertical padding around a 32px control is what takes the row
          // from 64px to 44px — the single largest saving after dropping the
          // AND/OR row.
          px={isCompact ? 3 : 4}
          py={isCompact ? 1.5 : 4}
          pt={staleKind ? 2 : undefined}
          position="relative"
        >
          <Column
            flex="1 0 auto"
            minWidth={0}
            sx={{
              "& > :not(:last-child)": { mb: isCompact ? 2 : 4 },
            }}
          >
            {children}
          </Column>
          {!isDisabled && (
            <Row align="center" justify="center" gap={2} flexShrink={0}>
              <FilterActionsMenu items={menuItems} />

              {condition && !isCompact && (
                <GenerateFilterInsightsButton condition={condition} />
              )}
              <DeleteButton isCompact={isCompact} onRemove={onRemove} />
            </Row>
          )}
        </Row>
        {footer}
      </Column>
    </>
  );
};

interface NestedAndOrConditionWrapperProps {
  children?: React.ReactNode;
  condition?: Condition;
  conditionType: ConditionType.And | ConditionType.Or;
  index: number;
  disableDrag?: boolean;
  forceDragStyles?: boolean;
  isDisabled?: boolean;
  onToggleAndOr: () => void;
  includeWaterfall?: boolean;
}

export const NestedAndOrConditionWrapper = (
  props: NestedAndOrConditionWrapperProps,
) => {
  const {
    children,
    index,
    condition,
    conditionType,
    disableDrag,
    isDisabled,
    onToggleAndOr,
    includeWaterfall,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const { ref } = useDropTarget({ index: index + 1, disable: disableDrag });
  const getContainerHeight = useCallback(() => {
    const height = containerRef.current?.offsetHeight;
    if (height) {
      if (includeWaterfall) {
        return height - waterfallHeight;
      }
      return height;
    }
    return undefined;
  }, [includeWaterfall]);
  return (
    <Column gap={4} mb={4} width="100%">
      <Row ref={containerRef}>
        <GroupIndicatorBar
          getContainerHeight={getContainerHeight}
          conditionType={conditionType}
          condition={condition}
          index={index}
          disableDrag={disableDrag}
        />
        {children}
      </Row>
      <Row ref={ref}>
        <AndOrToggleButton
          isDisabled={isDisabled}
          conditionType={conditionType}
          onClick={onToggleAndOr}
        />
      </Row>
    </Column>
  );
};

interface NestedRelationConditionWrapperProps {
  children?: React.ReactNode;
  onRemove?: () => void;
  isFirstCondition: boolean;
  isDisabled?: boolean;
  isWrappedWithAndCondition?: boolean;
  onToggleWrappingConditionType(
    conditionType: ConditionType.And | ConditionType.Or,
  ): void;
}

export const NestedRelationConditionWrapper = (
  props: NestedRelationConditionWrapperProps,
) => {
  const {
    children,
    onRemove,
    isFirstCondition,
    isDisabled,
    isWrappedWithAndCondition,
    onToggleWrappingConditionType,
  } = props;
  return (
    <>
      {!isFirstCondition && (
        <AndOrToggleButton
          isDisabled={isDisabled}
          conditionType={
            isWrappedWithAndCondition ? ConditionType.And : ConditionType.Or
          }
          onClick={() =>
            onToggleWrappingConditionType(
              isWrappedWithAndCondition ? ConditionType.Or : ConditionType.And,
            )
          }
        />
      )}
      <Row
        p={4}
        gap={2}
        borderWidth="hairline"
        borderStyle="solid"
        borderColor="base.border"
        borderRadius="md"
        align="center"
        justifyContent="space-between"
      >
        <Column gap={2}>{children}</Column>
        {onRemove && <XButton onRemove={onRemove} />}
      </Row>
    </>
  );
};
